import { useMemo } from "react"
import { AxisValue, DashBoardConditions } from "features/dashBoard/types"
import { CommonGraphStyle, filter, selectedItemStyle } from "../../common"
import { useTimeLineGraphOption, TimeOperations } from "./options"
import { DashBoardValue } from "features/dashBoard/hooks"
import { dateStampFormat } from "shared/util"
import { DownLoadData, DownLoadHeader, DownLoadRecord } from "components/download/csv/types"

export type BarData = {
	key: string,
	name: string,
	value: number,
	itemStyle?: CommonGraphStyle
}
export type TimeLineData = {
	time: Date
	value: number
	total: number
}
export type SeriesData = {
  code: string
  name?: string
  total: number
	timeLine: TimeLineData[]
	bar: BarData[]
}

type useTimeLineGraphModelProps<T> = {
  dashBoard: DashBoardValue<T>
	dataList: T[] | null
	conditions: DashBoardConditions,
	graphOption: ReturnType<typeof useTimeLineGraphOption<T>>
}
export const useTimeLineGraphModel = <T,>({
	dashBoard: {
		subSource: {
			lv: { dataState: lvLogListState },
			mrp: { dataState: mrpLogListState }
		}
	},
	dataList,
	conditions,
	graphOption: {
		configure, targetList,
    timeDef, by, valueDef, stackDef, total
  }
}: useTimeLineGraphModelProps<T>) => useMemo(
	() => {
		if (!dataList || dataList.length === 0) return null
		if (!timeDef?.key || !timeDef.time || !valueDef?.key) return null

		const operation = TimeOperations[by]
		if (!operation) return null

		const stackFunc = stackDef ? stackDef.val : configure.stack

		let minTime: Date | null = null
		let maxTime: Date | null = null
		const seriesList: SeriesData[] = []
		for (const record of dataList) {
			const time = timeDef.time(record)
			if (!time) continue

			const value = valueDef.val(record)
			if (value === undefined || value === 0 || !filter(record, configure, conditions, timeDef.key, targetList)) continue

			const stack = stackFunc ? stackFunc(record) : { code: '***', name: '' }
			if (!stack) continue

			const stackList = Array.isArray(stack) ? stack : [stack]
			const timeList: Date[] = Array.isArray(time) ? time : [time]
			for (const stack of stackList) {
				let series = seriesList.find((series) => series.code === stack.code)
				if (!series) {
					series = {
						code: stack.code,
						name: stack.name,
						total: 0,
						timeLine: [],
						bar: []
					}
					seriesList.push(series)
				}
				series.total = series.total + value

				for (const time of timeList) {
					series.timeLine.push({ time, value, total: 0 })
					if (minTime === null || minTime.getTime() > time.getTime()) minTime = time
					if (maxTime === null || maxTime.getTime() < time.getTime()) maxTime = time
				}
			}
		}
		if (total) {
			if (timeDef.source === 'mrp' && timeDef.byMrpLog !== undefined) {
				const byMrpLog = timeDef.byMrpLog
				const timeLine = mrpLogListState.data?.list.flatMap(
					(log) => {
						const time = byMrpLog(log)
						if (!time) return []
						if (minTime === null || minTime.getTime() > time.getTime()) minTime = time
						if (maxTime === null || maxTime.getTime() < time.getTime()) maxTime = time
						return { time, value: 1, total: 0 }
					}
				) || []
				if (timeLine.length > 0) {
					seriesList.push({
						code: 'all',
						name: '全体',
						total: 0,
						timeLine,
						bar: []
					})
				}
			} else if (timeDef.source === 'lv' && timeDef.byLvLog !== undefined) {
				const byLvLog = timeDef.byLvLog
				const timeLine = lvLogListState.data?.list.flatMap(
					(log) => {
						const time = byLvLog(log)
						if (!time) return []
						if (minTime === null || minTime.getTime() > time.getTime()) minTime = time
						if (maxTime === null || maxTime.getTime() < time.getTime()) maxTime = time
						return { time, value: 1, total: 0 }
					}
				) || []
				if (timeLine.length > 0) {
					seriesList.push({
						code: 'all',
						name: '全体',
						total: 0,
						timeLine,
						bar: []
					})
				}
			}
		}
		if (!minTime || !maxTime) return null

		seriesList.sort((a, b) => a.code.localeCompare(b.code))
		seriesList.forEach((series) => {
			series.timeLine.sort((a, b) => a.time.getTime() - b.time.getTime())
			let sum = 0
			series.timeLine.forEach((it) => {
				sum += it.value
				it.total = sum
			})
		})
		const condition = conditions[timeDef.key]
		const bars = operation.buildBars(minTime, maxTime)
		seriesList.forEach((series) => series.bar = bars.map(
			(time) => {
				const timeValue = operation.val(time)
				const ax: AxisValue = { code: dateStampFormat(time) }
				return {
					key: dateStampFormat(time),
					name: operation.format(time) || '??',
					value: series.timeLine.reduce(
						(total, it) => (operation.val(it.time) === timeValue) ? (total + it.value) : total,
						0
					),
					itemStyle: selectedItemStyle(condition, ax)
				}
			}
		))
		return {
			operation,
			seriesList, bars,
			minTime: operation.topTime(minTime),
			maxTime: operation.lastTime(maxTime),
			csvBuilder: (): DownLoadData | undefined => {
				if (!timeDef?.key || !valueDef?.key) return undefined

				const timeKey = timeDef.key
				const valueKey = valueDef.key
				const unit = valueDef.unit

				const header: DownLoadHeader[] = [
					{ title: timeDef.title, key: timeKey },
					{ title: valueDef.title, key: valueKey, unit }
				]
				seriesList.forEach((series) => {
					header.push({
						title: series.name ?? '',
						key: series.code === '***' ? '' : series.code,
						unit: valueDef.unit
					})
				})
				const body = bars.map(
					(time, i) => {
						const row: DownLoadRecord = {}
						row[timeKey] = dateStampFormat(time)
						let total = 0
						seriesList.forEach((series) => {
							const data = series.bar[i]
							if (data.value > 0) {
								row[series.code] = data.value
								if (series.code !== "all") total += data.value
							}
						})
						row[valueKey] = total
						return row
					}
				)
				return { header, body }
			}
		}
	},
	[
		lvLogListState, mrpLogListState, 
		dataList, configure, conditions, targetList,
		timeDef, by, valueDef, stackDef, total
	]
)
